import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Instagram } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "How to Grow on Instagram in India (2026 Creator Guide)";
const DESC =
  "A practical 2026 guide to growing an Instagram audience in India: picking a niche, Reels formats that travel, posting cadence, and how creators actually earn.";
const URL = "https://rajshamani.life/guides/how-to-grow-on-instagram-india";
const OG = OG_ABOUT;

const steps = [
  {
    title: "1. Pick a niche narrow enough to be recognised",
    detail:
      "\"Business\" is not a niche; \"how small Indian D2C brands price their products\" is. A narrow niche makes your account predictable — people follow because they know what the next post will be about.",
  },
  {
    title: "2. Choose one repeatable format",
    detail:
      "Most accounts that grow fast in India repeat a single format: talking-head Reels, screen-recorded breakdowns, street interviews or carousels. Repetition compounds because the algorithm and the audience both learn what you do.",
  },
  {
    title: "3. Write the first two seconds like a headline",
    detail:
      "Retention decides distribution. Open with the claim, the number or the tension — never with an introduction. If the hook needs context to make sense, it is not a hook.",
  },
  {
    title: "4. Post at a cadence you can hold for six months",
    detail:
      "Four to five Reels a week sustained beats fifteen for two weeks and then silence. Batch-record so a bad week does not break the streak.",
  },
  {
    title: "5. Use language your audience actually speaks",
    detail:
      "Hinglish outperforms formal English for most mass-market Indian niches, while pure English works better for B2B, SaaS and investing content. Match the language to the buyer, not to your preference.",
  },
  {
    title: "6. Read retention, not likes",
    detail:
      "In Insights, watch average watch time, retention graph shape and shares. Shares and saves predict reach; likes mostly follow reach after the fact.",
  },
  {
    title: "7. Build an off-platform destination",
    detail:
      "A newsletter, WhatsApp community or website turns rented reach into something you own. Every account that monetises well has somewhere to send people.",
  },
];

const monetisation = [
  {
    title: "Brand deals",
    detail:
      "The largest income source for most Indian creators. Rates depend on niche and audience quality far more than follower count — a 30k finance account can out-earn a 300k meme page.",
  },
  {
    title: "Affiliate and commerce",
    detail:
      "Amazon, Flipkart and D2C affiliate programs work when your content already recommends products. Best for review, gadget, beauty and fitness niches.",
  },
  {
    title: "Your own product",
    detail:
      "Courses, templates, communities or a physical product. Lowest ceiling to start, highest margin later, and it is the path most creator-founders eventually take.",
  },
  {
    title: "Services",
    detail:
      "Consulting, content retainers or agency work sold to the businesses watching you. The fastest to revenue if your niche is B2B.",
  },
];

const faq = [
  {
    q: "How long does it take to grow on Instagram in India?",
    a: "Most accounts that grow consistently take three to six months of steady posting before reach becomes reliable. Occasional viral spikes happen sooner, but they rarely convert into retained followers unless the account already has a clear, repeatable format.",
  },
  {
    q: "How many followers do you need to earn money on Instagram in India?",
    a: "Brands regularly work with creators from around 10,000 followers when the niche is specific and engagement is genuine. Niche relevance and audience trust matter far more than raw follower count.",
  },
  {
    q: "Are Reels still the fastest way to grow in 2026?",
    a: "Yes for reach. Reels remain the main discovery surface on Instagram in India, while carousels and stories do most of the work in converting new viewers into followers and buyers.",
  },
  {
    q: "How often should I post on Instagram to grow?",
    a: "Four to five posts a week, sustained for months, is the practical target. Consistency over a long window matters more than volume in a short burst.",
  },
  {
    q: "Should I post in Hindi, English or Hinglish?",
    a: "Match the language to your audience. Hinglish usually reaches the widest Indian audience, Hindi performs strongly in tier-2 and tier-3 markets, and English suits B2B, SaaS, investing and global topics.",
  },
];

export const Route = createFileRoute("/guides/how-to-grow-on-instagram-india")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Guide to growing on Instagram in India", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${URL}#article`,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          image: OG,
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          author: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: InstagramGuide,
});

function InstagramGuide() {
  return (
    <div>
      <PageBreadcrumb current="How to Grow on Instagram in India" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Instagram size={14} /> Creator guide
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            How to grow on <span className="text-gradient-gold">Instagram in India</span>
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Growth on Instagram in India comes from a narrow niche, one repeatable format, hooks
            written for the first two seconds, and a cadence you can hold for six months. This guide
            covers each of those, plus how creators here actually turn reach into income.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">The seven steps</h2>
        <div className="space-y-6">
          {steps.map((s) => (
            <div key={s.title} className="p-6 rounded-xl border border-border bg-card">
              <h3 className="font-bold mb-2">{s.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{s.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">
          How Indian creators turn reach into income
        </h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {monetisation.map((m) => (
            <div key={m.title} className="p-6 rounded-xl border border-border bg-card">
              <h3 className="font-bold mb-2">{m.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{m.detail}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-muted-foreground max-w-3xl leading-relaxed">
          For a worked example, see how{" "}
          <Link to="/raj-shamani-income" className="text-gold underline underline-offset-4">
            Raj Shamani&apos;s income
          </Link>{" "}
          is split across media, brand deals, speaking and businesses — and how the{" "}
          <Link to="/raj-shamani-podcast" className="text-gold underline underline-offset-4">
            Figuring Out podcast
          </Link>{" "}
          became the distribution engine behind all of it.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <AionCta />
      <RelatedPages pages={["podcast", "book", "about", "ventures", "aion", "contact"]} />
    </div>
  );
}
